import { handleResponse, request } from "@/hooks/request";
import type { ApiResponse } from "@/types/api";
import type { AssessmentScoreInput, AssessmentStep, Assessor } from "@/types/assessment";
import type { CandidatesUnderAssessment } from "@/types/candidate";
import type { Competency } from "@/types/competency";
import type { AssignmentScorecards, Scorecard } from "@/types/scorecard";

type UpdateParams = {
  step_id: string;
  assignment_id: string;
  title: string;
};

const AssessmentApi = {
  getAssessmentUsers: (filter: any, abort_signal?: AbortSignal) => {
    const search = new URLSearchParams(filter);
    return request(`/api/assessment/user?${search.toString()}`, {
      signal: abort_signal,
    }).then(handleResponse<Assessor[]>);
  },
  addAssessmentUser: (
    assessor_email: string,
    assignment_id: string,
    assessment_step_id: string,
    abort_signal?: AbortSignal,
  ) => {
    return request("/api/assessment/user", {
      method: "post",
      signal: abort_signal,
      body: JSON.stringify({
        email: assessor_email,
        assignment_id,
        assessment_step_id,
      }),
    }).then(handleResponse<Assessor>);
  },
  removeAssessmentUser: (
    assessor_email: string,
    assignment_id: string,
    assessment_step_id: string,
    abort_signal?: AbortSignal,
  ) => {
    return request("/api/assessment/user", {
      method: "delete",
      signal: abort_signal,
      body: JSON.stringify({
        email: assessor_email,
        assignment_id,
        assessment_step_id,
      }),
    }).then(handleResponse<Assessor>);
  },
  getCompetencies: (abort_signal?: AbortSignal): Promise<ApiResponse<Competency[]>> => {
    return request("/api/competency", {
      signal: abort_signal,
    })
      .then(handleResponse<Competency[]>)
      .then((response) => ({
        data: response.data ?? undefined, // Convert null to undefined to match ApiResponse type
        error: response.error?.detail_text,
        status: typeof response.error?.status === "number" ? response.error.status : undefined,
      }))
      .catch((error) => ({
        data: undefined,
        error: error.message,
        status: 500,
      }));
  },
  getAssessmentSteps: (assignment_id: string, abort_signal?: AbortSignal) => {
    return request(`/api/assessment/assignment/${assignment_id}/step`, {
      signal: abort_signal,
    }).then(handleResponse<AssessmentStep[]>);
  },
  addAssessmentStep: (assignment_id: string, title: string, abort_signal?: AbortSignal) => {
    return request("/api/assessment/step", {
      method: "post",
      signal: abort_signal,
      body: JSON.stringify({
        assignment_id,
        title,
      }),
    }).then(handleResponse<AssessmentStep>);
  },
  updateAssessmentStep: (
    { step_id, assignment_id, title }: UpdateParams,
    abort_signal?: AbortSignal,
  ) => {
    return request(`/api/assessment/step/${step_id}`, {
      method: "post",
      signal: abort_signal,
      body: JSON.stringify({
        title,
        assignment_id,
      }),
    }).then(handleResponse<AssessmentStep>);
  },
  removeAssessmentStep: (step_id: string, abort_signal?: AbortSignal) => {
    return request(`/api/assessment/step/${step_id}`, {
      method: "delete",
      signal: abort_signal,
    }).then(handleResponse);
  },
  changeAssessmentStepOrdering: (
    step_id: string,
    increase: boolean,
    abort_signal?: AbortSignal,
  ) => {
    return request(`/api/assessment/step/${step_id}/ordering/${increase ? 1 : -1}`, {
      method: "put",
      signal: abort_signal,
    }).then(handleResponse<AssessmentStep[]>);
  },
  addCompetencyToAssessmentStep: (
    step_id: string,
    competency_id: string,
    abort_signal?: AbortSignal,
  ) => {
    return request(`/api/assessment/step/${step_id}/competency/${competency_id}`, {
      method: "post",
      signal: abort_signal,
    }).then(handleResponse<AssessmentStep>);
  },
  removeCompetencyFromAssessmentStep: (
    step_id: string,
    competency_id: string,
    abort_signal?: AbortSignal,
  ) => {
    return request(`/api/assessment/step/${step_id}/competency/${competency_id}`, {
      method: "delete",
      signal: abort_signal,
    }).then(handleResponse<AssessmentStep>);
  },
  getUserAssessment: (candidate_id: string, abort_signal?: AbortSignal) => {
    return request(`/api/assessment/candidate/${candidate_id}`, {
      signal: abort_signal,
    }).then(handleResponse<Scorecard>);
  },
  getAssessments: (assignment_id: string, abort_signal?: AbortSignal) => {
    return request(`/api/assessment/assignment/${assignment_id}`, {
      signal: abort_signal,
    }).then(handleResponse<CandidatesUnderAssessment>);
  },
  getCandidateScorecards: (candidate_id: string, abort_signal?: AbortSignal) => {
    return request(`/api/assessment/candidate/${candidate_id}/scorecard`, {
      signal: abort_signal,
    }).then(handleResponse<Scorecard>);
  },
  getAssignmentScorecards: (assignment_id: string, abort_signal?: AbortSignal) => {
    return request(`/api/assessment/assignment/${assignment_id}/scorecard`, {
      signal: abort_signal,
    }).then(handleResponse<AssignmentScorecards>);
  },
  addAssessmentCandidate: (candidate_id: string, abort_signal?: AbortSignal) => {
    return request(`/api/assessment/candidate/${candidate_id}`, {
      method: "post",
      signal: abort_signal,
    }).then(handleResponse);
  },
  removeAssessmentCandidate: (candidate_id: string, abort_signal?: AbortSignal) => {
    return request(`/api/assessment/candidate/${candidate_id}`, {
      method: "delete",
      signal: abort_signal,
    }).then(handleResponse);
  },
  scoreCandidate: (
    candidate_id: string,
    competencies: AssessmentScoreInput[],
    abort_signal?: AbortSignal,
  ) => {
    return request("/api/candidate/assessment", {
      method: "post",
      body: JSON.stringify({ candidate_id, competencies }),
      signal: abort_signal,
    }).then(handleResponse<Scorecard>);
  },
};

export default AssessmentApi;
